import { has, isArray, isUndefined } from 'lodash';

import { LoggerServiceInterface } from '../../../common/services';
import {
  CmsHelperInterface,
  CmsServiceInterface,
  MapBoolContentParams,
  MapContentFieldParams,
  MapTextLanguageFieldsParams,
  MapValueFromEnumParams,
} from '../interfaces';
import { Component, Page, PageContent } from '../types';
import Mapper from './mapper';

abstract class BaseMapper extends Mapper {
  constructor(
    protected readonly cmsService: CmsServiceInterface,
    protected readonly cmsHelper: CmsHelperInterface,
    protected readonly loggerService: LoggerServiceInterface,
    protected readonly page: Page,
  ) {
    super();
  }

  abstract generateComponent(): Promise<Component>;

  protected async renderChildrenFromId(): Promise<Component[]> {
    return this.page.children_ids?.length
      ? await this.cmsService.renderPagesComponentsById(
          this.page.children_ids,
          this.importMapper,
        )
      : [];
  }

  protected async renderChildren(): Promise<Component[]> {
    return this.page.children?.length
      ? await this.cmsService.renderPagesComponents(
          this.page.children,
          this.importMapper,
        )
      : [];
  }

  protected mapValueFromEnum<T>({
    typeEnum,
    content = this.page.content,
    contentKey,
    keyPrefix = 'option',
    defaultKey = 'option1',
    defaultValue = null,
  }: MapValueFromEnumParams): T {
    const enumKey = has(content, contentKey)
      ? (`${keyPrefix}${this.mapContentField({
          content,
          contentKey,
          defaultValue: '',
        })}` as keyof typeof typeEnum)
      : defaultKey;

    return typeEnum[enumKey] || defaultValue;
  }

  protected mapContentField<T>({
    content = this.page.content,
    contentKey,
    defaultValue,
    returnFirstofArray = true,
  }: MapContentFieldParams<T>): T {
    if (!has(content, contentKey)) return defaultValue as T;

    if (returnFirstofArray && isArray(content[contentKey]))
      return (content as PageContent<T[]>)[contentKey].pop() as T;
    else return content[contentKey] as T;
  }

  protected mapBoolContent({
    content = this.page.content,
    contentKey,
    defaultValue = false,
  }: MapBoolContentParams): boolean {
    if (!has(content, contentKey)) return defaultValue;
    return content[contentKey] === '1';
  }

  protected mapTextLanguageFields<T = unknown>({
    content = this.page.content,
    fieldsToMap,
  }: MapTextLanguageFieldsParams<T>): T {
    const { locale } = this.cmsHelper;

    const mappedFields = {} as T;

    fieldsToMap.forEach(({ key, mappedKey, defaultValue }) => {
      if (
        has(content, `${key}.${locale}`) &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (content as PageContent<any>)[key][locale].length > 0
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mappedFields[mappedKey] = (content as PageContent<any>)[key][locale];
      } else if (!isUndefined(defaultValue)) {
        mappedFields[mappedKey] = defaultValue;
      }
    });

    return mappedFields;
  }

  protected toPascalCase(value: string): string {
    return value
      .replace(/(module_)|(module-)/, '')
      .replace(/(^\w|-\w)|(^\w|_\w)/g, this.clearAndUpper);
  }

  private clearAndUpper(value: string): string {
    return value.replace(/(-)|(_)/, '').toUpperCase();
  }
}

export default BaseMapper;
